export const dataSources = [
  "customer",
  "merchant",
  "redo_for_merchant",
  "redo_internal",
  "third_party",
  "mixed",
  "operational",
] as const;
export type DataSource = (typeof dataSources)[number];
const dataSourceNames: ReadonlySet<string> = new Set(dataSources);

export const dataKinds = [
  "email",
  "phone",
  "ip_address",
  "user_agent",
  "username",
  "social_handle",
  "person_name",
  "company_name",
  "resource_label",
  "address_line",
  "geo_coarse",
  "geo_coordinate",
  "internal_id",
  "external_id_resource",
  "external_id_person",
  "external_id_compound",
  "personal_external_id",
  "tracking_id",
  "government_id",
  "device_id",
  "session_id",
  "capability_code",
  "serial_or_lot",
  "text",
  "model_prompt",
  "system_error_text",
  "product_catalog_text",
  "credential",
  "hashed_credential",
  "payment_instrument",
  "card_last4",
  "payment_token",
  "card_expiry",
  "public_url",
  "private_url",
  "filename",
  "file_bytes",
  "embedding_vector",
  "timestamp",
  "date_of_birth",
  "money",
  "quantity",
  "dimension",
  "enum_or_status",
  "flag",
  "product_sku",
  "untyped_payload",
] as const;
export type DataKind = (typeof dataKinds)[number];
const dataKindNames: ReadonlySet<string> = new Set(dataKinds);

/**
 * Legacy pgmask shared-type names. These are mask implementations, not kinds.
 * Canonical list: redo/gdpr/pgmask/generation/src/semantic-types.ts.
 */
export const semanticTypes = [
  "email",
  "phone",
  "person_name",
  "address_line",
  "company_name",
  "external_id",
  "free_text",
] as const;
export type SemanticType = (typeof semanticTypes)[number];
const semanticTypeNames: ReadonlySet<string> = new Set(semanticTypes);

export type LegacyPrivacyPolicy = "nonPii" | "redact" | SemanticType;

/** Source is required when the kind has a source-aware mask policy. */
export type SourcedKind =
  | "email"
  | "phone"
  | "ip_address"
  | "user_agent"
  | "username"
  | "social_handle"
  | "person_name"
  | "company_name"
  | "address_line"
  | "text";
const SOURCED_KINDS_ARRAY = [
  "email",
  "phone",
  "ip_address",
  "user_agent",
  "username",
  "social_handle",
  "person_name",
  "company_name",
  "address_line",
  "text",
] as const;
export const SOURCED_KINDS: ReadonlySet<SourcedKind> = new Set(
  SOURCED_KINDS_ARRAY,
);

export interface PrivacyClassifier {
  readonly kind: DataKind;
  readonly source?: DataSource;
}

export type PrivacyPolicy = PrivacyClassifier | LegacyPrivacyPolicy;
export type PrivacyValueType =
  | "string"
  | "number"
  | "boolean"
  | "null"
  | "unknown";
export type PrivacyNode =
  | { kind: "leaf"; policy: PrivacyPolicy; valueType: PrivacyValueType }
  | { kind: "object"; fields: Record<string, PrivacyNode> }
  | { kind: "array"; element: PrivacyNode }
  | { kind: "tuple"; elements: PrivacyNode[]; rest?: PrivacyNode }
  | {
      kind: "union";
      variants: PrivacyNode[];
      discriminator?: string;
      variantValues?: string[];
    };

export interface JsonPrivacySchema {
  readonly _zod: object;
}

export type PrivacyTraversal =
  | { kind: "structured" }
  | { kind: "jsonShape"; schema: JsonPrivacySchema };

export type PrivacyAnnotation = PrivacyPolicy | PrivacyTraversal;

export interface SourcedPrivacy {
  readonly customer: PrivacyClassifier;
  readonly merchant: PrivacyClassifier;
  readonly redoForMerchant: PrivacyClassifier;
  readonly redoInternal: PrivacyClassifier;
  readonly thirdParty: PrivacyClassifier;
  readonly mixed: PrivacyClassifier;
  readonly operational: PrivacyClassifier;
}

const objectAnnotations = new WeakMap<object, PrivacyAnnotation>();
const objectConflicts = new WeakSet<object>();
const pathAnnotations = new WeakMap<
  object,
  Readonly<Record<string, PrivacyAnnotation>>
>();
const pathConflicts = new WeakMap<object, Set<string>>();

function classify(kind: DataKind, source?: DataSource): PrivacyClassifier {
  return source === undefined ? { kind } : { kind, source };
}

function sourced(kind: SourcedKind): SourcedPrivacy {
  return {
    customer: { kind, source: "customer" },
    merchant: { kind, source: "merchant" },
    redoForMerchant: { kind, source: "redo_for_merchant" },
    redoInternal: { kind, source: "redo_internal" },
    thirdParty: { kind, source: "third_party" },
    mixed: { kind, source: "mixed" },
    operational: { kind, source: "operational" },
  };
}

function externalId(
  referent: "resource" | "person" | "compound",
): PrivacyClassifier {
  return { kind: `external_id_${referent}` as DataKind };
}

const id = {
  internal: classify("internal_id"),
  external: {
    resource: externalId("resource"),
    person: externalId("person"),
    compound: externalId("compound"),
  },
  personalExternal: classify("personal_external_id"),
  tracking: classify("tracking_id"),
  government: classify("government_id"),
  device: classify("device_id"),
  session: classify("session_id"),
  capability: classify("capability_code"),
  serialOrLot: classify("serial_or_lot"),
} as const;

export const privacy = {
  email: sourced("email"),
  phone: sourced("phone"),
  ipAddress: sourced("ip_address"),
  userAgent: sourced("user_agent"),
  username: sourced("username"),
  socialHandle: sourced("social_handle"),
  person_name: sourced("person_name"),
  company_name: sourced("company_name"),
  resourceLabel: classify("resource_label"),
  address_line: sourced("address_line"),
  geo: {
    coarse: classify("geo_coarse"),
    coordinate: classify("geo_coordinate"),
  },
  id,
  text: sourced("text"),
  modelPrompt: classify("model_prompt"),
  systemErrorText: classify("system_error_text"),
  productCatalogText: classify("product_catalog_text"),
  credential: classify("credential"),
  hashedCredential: classify("hashed_credential"),
  paymentInstrument: classify("payment_instrument"),
  cardLast4: classify("card_last4"),
  paymentToken: classify("payment_token"),
  cardExpiry: classify("card_expiry"),
  publicUrl: classify("public_url"),
  privateUrl: classify("private_url"),
  filename: classify("filename"),
  fileBytes: classify("file_bytes"),
  embeddingVector: classify("embedding_vector"),
  timestamp: classify("timestamp"),
  dateOfBirth: classify("date_of_birth"),
  money: classify("money"),
  quantity: classify("quantity"),
  dimension: classify("dimension"),
  enumOrStatus: classify("enum_or_status"),
  flag: classify("flag"),
  productSku: classify("product_sku"),
  untypedPayload: classify("untyped_payload"),
  /** @deprecated Unpack into a kind. Preserved so existing schemas keep compiling. */
  nonPii: "nonPii" as LegacyPrivacyPolicy,
  /** @deprecated Use a kind. */
  redact: "redact" as LegacyPrivacyPolicy,
  structured: { kind: "structured" } as PrivacyTraversal,
  jsonShape(schema: JsonPrivacySchema): PrivacyAnnotation {
    return { kind: "jsonShape", schema };
  },
  object<T extends object>(definition: T, annotation?: PrivacyAnnotation): T {
    const resolved: PrivacyAnnotation = annotation ?? { kind: "structured" };
    const previous = objectAnnotations.get(definition);
    if (
      previous !== undefined &&
      !equalPrivacyAnnotations(previous, resolved)
    ) {
      objectConflicts.add(definition);
    } else {
      objectAnnotations.set(definition, resolved);
    }
    return definition;
  },
  paths<T extends object>(
    schema: T,
    paths: Readonly<Record<string, PrivacyAnnotation>>,
  ): T {
    const previous: Readonly<Record<string, PrivacyAnnotation>> =
      pathAnnotations.get(schema) ?? Object.create(null);
    const combined: Record<string, PrivacyAnnotation> = Object.assign(
      Object.create(null),
      previous,
    );
    for (const [path, annotation] of Object.entries(paths)) {
      const old = Object.hasOwn(previous, path) ? previous[path] : undefined;
      if (old !== undefined && !equalPrivacyAnnotations(old, annotation)) {
        const conflicts = pathConflicts.get(schema) ?? new Set<string>();
        conflicts.add(path);
        pathConflicts.set(schema, conflicts);
      } else {
        combined[path] = annotation;
      }
    }
    pathAnnotations.set(schema, combined);
    return schema;
  },
} as const;

const opaqueKinds: ReadonlySet<DataKind> = new Set([
  "untyped_payload",
  "credential",
  "hashed_credential",
  "payment_instrument",
  "payment_token",
  "file_bytes",
  "embedding_vector",
]);

export function isSourcedKind(kind: DataKind): kind is SourcedKind {
  return SOURCED_KINDS.has(kind as SourcedKind);
}

export function hasObjectPrivacyConflict(value: object): boolean {
  return objectConflicts.has(value);
}
export function getObjectPrivacy(value: object): PrivacyAnnotation | undefined {
  return objectAnnotations.get(value);
}
export function getPathPrivacy(
  value: object,
): Readonly<Record<string, PrivacyAnnotation>> {
  return pathAnnotations.get(value) ?? Object.create(null);
}
/** Conflicts are enforced only after the authoritative schema is selected. */
export function getPathPrivacyConflicts(value: object): readonly string[] {
  return [...(pathConflicts.get(value) ?? [])].sort();
}

export function isPrivacyClassifier(
  value: unknown,
): value is PrivacyClassifier {
  if (
    typeof value !== "object" ||
    value === null ||
    !("kind" in value) ||
    typeof value.kind !== "string" ||
    !dataKindNames.has(value.kind)
  ) {
    return false;
  }
  if (
    "source" in value &&
    value.source !== undefined &&
    (typeof value.source !== "string" || !dataSourceNames.has(value.source))
  ) {
    return false;
  }
  if (
    isSourcedKind(value.kind as DataKind) &&
    (!("source" in value) || value.source === undefined)
  ) {
    return false;
  }
  return true;
}

export function isLegacyPrivacyPolicy(
  value: unknown,
): value is LegacyPrivacyPolicy {
  return (
    value === "nonPii" ||
    value === "redact" ||
    (typeof value === "string" && semanticTypeNames.has(value))
  );
}

export function isLeafPrivacyAnnotation(
  value: unknown,
): value is PrivacyPolicy {
  return isPrivacyClassifier(value) || isLegacyPrivacyPolicy(value);
}

export function allowsOpaquePrivacy(value: unknown): boolean {
  return isPrivacyClassifier(value) && opaqueKinds.has(value.kind);
}

export function normalizePrivacyPolicy(policy: PrivacyPolicy): PrivacyPolicy {
  if (typeof policy === "string") {
    return policy;
  }
  const { kind, source } = policy;
  if (source === undefined) {
    return { kind };
  }
  return { kind, source };
}

export function privacyPolicyKey(policy: PrivacyPolicy): string {
  if (typeof policy === "string") {
    return policy;
  }
  return policy.source === undefined
    ? policy.kind
    : `${policy.kind}:${policy.source}`;
}

export function equalPrivacyAnnotations(
  a: PrivacyAnnotation,
  b: PrivacyAnnotation,
): boolean {
  if (a === b) {
    return true;
  }
  if (isPrivacyClassifier(a) && isPrivacyClassifier(b)) {
    return (
      a.kind === b.kind && (a.source ?? undefined) === (b.source ?? undefined)
    );
  }
  return (
    typeof a === "object" &&
    typeof b === "object" &&
    a.kind === b.kind &&
    (a.kind === "structured" ||
      (a.kind === "jsonShape" &&
        b.kind === "jsonShape" &&
        a.schema === b.schema))
  );
}

export function isPrivacyAnnotation(
  value: unknown,
): value is PrivacyAnnotation {
  if (isLeafPrivacyAnnotation(value)) {
    return true;
  }
  return (
    typeof value === "object" &&
    value !== null &&
    "kind" in value &&
    (value.kind === "structured" ||
      (value.kind === "jsonShape" &&
        "schema" in value &&
        typeof value.schema === "object" &&
        value.schema !== null &&
        "_zod" in value.schema &&
        typeof value.schema._zod === "object" &&
        value.schema._zod !== null))
  );
}
